/**
 * MechaGuard - LeaderboardPanel (排行榜面板)
 *
 * 按关卡分页显示本地前 10 名，并附上计分规则说明 ——
 * 玩家能看懂分是怎么来的，才会有动力去刷分。
 */

import { LEVELS } from '../maps/MapData.js';
import { SCORE_RULES } from '../systems/LeaderboardSystem.js';
import { formatTime, formatNumber } from './format.js';

const COLUMNS = ['#', '分数', '波次', '击杀', '生命', '用时'];

export class LeaderboardPanel {
  constructor() {
    this._root = null;
    this._els = {};
    this._handlers = {};

    /** @type {import('../systems/LeaderboardSystem.js').LeaderboardSystem|null} */
    this._leaderboard = null;
    this._activeLevel = LEVELS[0].id;
  }

  /**
   * @param {{onClose?:Function}} handlers
   */
  init(handlers = {}) {
    this._handlers = handlers;

    const root = document.getElementById('leaderboard-panel');
    if (!root) return;

    this._root = root;
    root.innerHTML = '';

    const box = document.createElement('div');
    box.className = 'overlay-content overlay-content--wide';

    const title = document.createElement('h2');
    title.className = 'overlay-title';
    title.textContent = '排行榜';

    const tabs = document.createElement('div');
    tabs.className = 'lb-tabs';

    const list = document.createElement('div');
    list.className = 'lb-list';

    const rules = document.createElement('details');
    rules.className = 'lb-rules';
    const rulesSummary = document.createElement('summary');
    rulesSummary.textContent = '计分规则';
    rules.appendChild(rulesSummary);
    for (const rule of SCORE_RULES) {
      const row = document.createElement('div');
      row.className = 'lb-rule-row';
      const label = document.createElement('span');
      label.textContent = rule.label;
      const value = document.createElement('span');
      value.textContent = rule.value;
      row.append(label, value);
      rules.appendChild(row);
    }

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'btn btn-primary menu-btn';
    close.textContent = '关闭';
    close.addEventListener('click', () => this._handlers.onClose?.());

    box.append(title, tabs, list, rules, close);
    root.appendChild(box);

    this._els = { tabs, list };
  }

  get isOpen() {
    return !!this._root && !this._root.classList.contains('hidden');
  }

  /**
   * @param {import('../systems/LeaderboardSystem.js').LeaderboardSystem} leaderboard
   * @param {string} [focusLevelId] 打开时默认选中的关卡
   */
  open(leaderboard, focusLevelId = null) {
    if (!this._root || !leaderboard) return;

    this._leaderboard = leaderboard;

    if (focusLevelId && LEVELS.some(l => l.id === focusLevelId)) {
      this._activeLevel = focusLevelId;
    }

    this._renderTabs();
    this._renderList();
    this._root.classList.remove('hidden');
  }

  close() {
    if (this._root) this._root.classList.add('hidden');
  }

  /* ===================================================
   * 渲染
   * =================================================== */

  _renderTabs() {
    const tabs = this._els.tabs;
    if (!tabs) return;

    tabs.innerHTML = '';

    for (const level of LEVELS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = level.id === this._activeLevel ? 'lb-tab active' : 'lb-tab';
      btn.textContent = `${level.icon} ${level.name}`;

      btn.addEventListener('click', () => {
        this._activeLevel = level.id;
        this._renderTabs();
        this._renderList();
      });

      tabs.appendChild(btn);
    }
  }

  _renderList() {
    const container = this._els.list;
    if (!container) return;

    container.innerHTML = '';

    const entries = this._leaderboard
      ? this._leaderboard.getEntries(this._activeLevel, 10)
      : [];

    if (entries.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'lb-empty';
      empty.textContent = '这一关还没有记录，去打一局吧';
      container.appendChild(empty);
      return;
    }

    // 表头
    const head = document.createElement('div');
    head.className = 'lb-row lb-head';
    for (const label of COLUMNS) {
      const cell = document.createElement('span');
      cell.textContent = label;
      head.appendChild(cell);
    }
    container.appendChild(head);

    entries.forEach((entry, index) => {
      const row = document.createElement('div');
      row.className = 'lb-row';
      if (index === 0) row.classList.add('lb-first');
      if (entry.victory) row.classList.add('lb-victory');

      const cells = [
        String(index + 1),
        formatNumber(entry.score),
        `${entry.wavesCleared}/${entry.totalWaves}`,
        String(entry.kills),
        String(entry.lives),
        formatTime(entry.elapsed),
      ];

      for (const text of cells) {
        const cell = document.createElement('span');
        cell.textContent = text;
        row.appendChild(cell);
      }

      // 通关的记录加个标记，方便一眼区分
      if (entry.victory) {
        const badge = document.createElement('span');
        badge.className = 'lb-badge';
        badge.textContent = '通关';
        row.appendChild(badge);
      }

      container.appendChild(row);
    });
  }
}
